import {
    faExclamationTriangle,
    faStar
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import PropTypes from "prop-types";
import * as qs from "query-string";
import React from "react";
import { history } from "../../store";
import styles from "../../styles/components/CompareDriversTable.css";
import Datatable from "../Datatable";
import PageLabel from "../PageLabel";
import { BENCHMARK_POINTS } from "./Drivers/info";

export const LegendItem = ({ icon, text }) => (
    <div className={styles.legendItem}>
        {icon || ""}
        <p className={styles.legendText}>{text}</p>
    </div>
);

LegendItem.propTypes = {
    icon: PropTypes.node.isRequired,
    text: PropTypes.string.isRequired
};

export const CompareDriversTableLegend = () => (
    <div className={styles.legend}>
        <LegendItem
            icon={
                <SignificanceIcon
                    isHigh={true}
                    extraClassName={styles.legendIcon}
                />
            }
            text="Significantly above benchmark"
        />
        <LegendItem
            icon={
                <SignificanceIcon
                    isHigh={false}
                    extraClassName={styles.legendIcon}
                />
            }
            text="Significantly below benchmark"
        />
    </div>
);

export const SignificanceIcon = ({ isHigh, extraClassName }) => (
    <FontAwesomeIcon
        icon={isHigh ? faStar : faExclamationTriangle}
        className={[
            styles.decoration,
            isHigh ? styles.high : styles.low,
            extraClassName || ""
        ].join(" ")}
        size="sm"
    />
);

SignificanceIcon.propTypes = {
    isHigh: PropTypes.bool.isRequired,
    extraClassName: PropTypes.string
};

export function BenchmarkCell({ benchmarkScore }) {
    return <div className={styles.cell}>{benchmarkScore} / 35</div>;
}

export function PageCell({ score, benchmarkScore, onClick }) {
    const diff = score - benchmarkScore;

    const classNames = [styles.cell, styles.pageCell];

    let decoration = null;
    if (diff <= -BENCHMARK_POINTS) {
        classNames.push(styles.low);
        decoration = <SignificanceIcon isHigh={false} />;
    } else if (diff >= BENCHMARK_POINTS) {
        classNames.push(styles.high);
        decoration = <SignificanceIcon isHigh={true} />;
    }

    return (
        <div className={classNames.join(" ")} onClick={onClick}>
            {score} {decoration}
        </div>
    );
}

export default function CompareDriversTable({
    drivers,
    benchmarks,
    pages,
    teamId
}) {
    const firstColumnWidthPct = 10;
    const columnWidthPct = (100 - firstColumnWidthPct) / drivers.length;

    const columns = [
        // the first column gives the page name
        {
            renderHeaderCell: () => <div className={styles.cell}>Page</div>,
            renderBodyCell: (page, index) => (
                <div className={styles.cell}>
                    {index === pages.length ? (
                        <p className={styles.benchmarkLabel}>
                            Industry Benchmark
                        </p>
                    ) : (
                        <PageLabel label={page.pageLabel} color={page.color} />
                    )}
                </div>
            ),
            cellWidthPercentage: firstColumnWidthPct
        },

        // 1 additional column for each of the drivers we want to display
        ...drivers.map(driver => {
            return {
                renderHeaderCell: () => (
                    <div className={styles.cell}>{driver}</div>
                ),
                renderBodyCell: (page, index) => (
                    <div className={styles.cell}>
                        {index === pages.length ? (
                            <BenchmarkCell
                                benchmarkScore={Math.round(
                                    benchmarks[driver].avg
                                )}
                            />
                        ) : (
                            <PageCell
                                benchmarkScore={Math.round(
                                    benchmarks[driver].avg
                                )}
                                score={Math.round(page.drivers[driver])}
                                color={page.color}
                                onClick={() => {
                                    const pathname = `/team/${teamId}/results/${
                                        page.wevoInfoId
                                    }/drivers`;
                                    const queryString = qs.stringify({
                                        selected: page.id,
                                        driver: driver
                                    });
                                    return history.push(
                                        `${pathname}?${queryString}`
                                    );
                                }}
                            />
                        )}
                    </div>
                ),
                cellWidthPercentage: columnWidthPct
            };
        })
    ];

    const data = [
        ...pages,
        {} // I need a row for the benchmark data, but I don't need there to be anything in there
    ];

    return (
        <div className={styles.compareDriversTable}>
            <Datatable
                columns={columns}
                data={data}
                rowClassName={idx =>
                    idx === pages.length ? styles.benchmarkRow : null
                }
            />
        </div>
    );
}

CompareDriversTable.propTypes = {
    teamId: PropTypes.any.isRequired,
    drivers: PropTypes.arrayOf(PropTypes.string).isRequired,
    benchmarks: PropTypes.objectOf(
        PropTypes.shape({
            avg: PropTypes.number.isRequired
        })
    ).isRequired,
    pages: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.any.isRequired,
            wevoInfoId: PropTypes.any.isRequired,
            pageLabel: PropTypes.string.isRequired,
            drivers: PropTypes.objectOf(PropTypes.number).isRequired,
            color: PropTypes.string.isRequired
        })
    ).isRequired
};



// WEBPACK FOOTER //
// ./src/components/Results/CompareDriversTable.js